import { Link } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { Radar, MapPin, Send, Bell, Zap, ShieldCheck, ArrowRight, Facebook, Linkedin, Instagram, Gift } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1563986768609-322da13575f3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Landing() {
  const { t } = useLang();

  const steps = [
    { icon: Send, t: t("how1_t"), d: t("how1_d") },
    { icon: Radar, t: t("how2_t"), d: t("how2_d") },
    { icon: Bell, t: t("how3_t"), d: t("how3_d") },
  ];

  return (
    <div>
      {/* Trial announcement bar */}
      <Link
        to="/register"
        data-testid="trial-banner"
        className="group block bg-primary text-primary-foreground"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center justify-center gap-3 text-sm font-medium">
          <Gift className="h-4 w-4 shrink-0" />
          <span className="text-center">{t("trial_banner")}</span>
          <span className="hidden sm:inline-flex items-center gap-1 font-semibold underline underline-offset-2 group-hover:gap-2 transition-all">
            {t("trial_banner_cta")} <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </Link>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-dots opacity-40" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center py-16 lg:py-24">
            <div className="animate-fade-up">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-muted-foreground mb-6">
                <Zap className="h-3.5 w-3.5 text-primary" /> {t("tagline")}
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none mb-6">
                {t("hero_title")}
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-xl">{t("hero_sub")}</p>
              <div className="flex flex-wrap gap-3">
                <Button asChild size="lg" data-testid="hero-feed-btn">
                  <Link to="/feed">{t("hero_cta")} <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" data-testid="hero-pricing-btn">
                  <Link to="/pricing">{t("hero_cta2")}</Link>
                </Button>
              </div>
              <div className="flex items-center gap-5 mt-10 text-muted-foreground">
                <span className="text-xs font-semibold uppercase tracking-widest">Sources</span>
                <Facebook className="h-5 w-5" />
                <Linkedin className="h-5 w-5" />
                <Instagram className="h-5 w-5" />
              </div>
            </div>
            <div className="relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
              <div className="absolute -inset-4 bg-primary/10 rounded-[2rem] blur-2xl" />
              <img
                src={HERO}
                alt="Freelancer"
                className="relative rounded-[2rem] shadow-2xl object-cover w-full h-[420px] border border-border"
              />
              <div className="absolute -bottom-5 -left-5 bg-card border border-border rounded-2xl shadow-lg p-4 flex items-center gap-3">
                <div className="grid place-items-center h-10 w-10 rounded-xl bg-emerald-500/10 text-emerald-600">
                  <Bell className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold">Nouvelle demande</p>
                  <p className="text-xs text-muted-foreground inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> Montréal · Plomberie</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-3 divide-x divide-border">
          {[["40+", t("stat_leads")], ["10", t("stat_sectors")], ["70+", t("stat_cities")]].map(([n, l]) => (
            <div key={l} className="py-10 text-center">
              <div className="font-display text-3xl sm:text-4xl font-extrabold text-primary">{n}</div>
              <div className="text-sm text-muted-foreground mt-1">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-center mb-14">{t("how_title")}</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <div key={s.t} className="rounded-2xl border border-border bg-card p-8 shadow-sm">
              <div className="grid place-items-center h-12 w-12 rounded-xl bg-primary/10 text-primary mb-5">
                <s.icon className="h-6 w-6" />
              </div>
              <div className="font-mono text-xs text-muted-foreground mb-2">0{i + 1}</div>
              <h3 className="font-display text-xl font-semibold mb-2">{s.t}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="rounded-3xl bg-primary text-primary-foreground p-10 lg:p-16 relative overflow-hidden">
          <div className="absolute inset-0 grid-dots opacity-10" />
          <div className="relative max-w-2xl">
            <ShieldCheck className="h-10 w-10 mb-5" />
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-4">{t("features_title")}</h2>
            <p className="text-primary-foreground/80 mb-8 text-lg">{t("hero_sub")}</p>
            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg" variant="secondary" data-testid="cta-register-btn">
                <Link to="/register">{t("get_started")}</Link>
              </Button>
              <Button asChild size="lg" variant="secondary" data-testid="cta-post-btn"
                className="bg-transparent border border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                <Link to="/post">{t("nav_post")}</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
